/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { ListViewProps } from './ListView.types';
import { StyledListView } from './ListView.styles';
import { HotelCard } from '../HotelCard';
import { Pagination } from '@marriott/mi-ui-library';
import { fetchHDData } from '../../modules/helpers';
import { MOBILE_NAVBAR_HEIGHT, NAVBAR_HEIGHT } from '../../modules/constants/constant';
import { phoenixLuxurySearchByGeoQueryList, phoenixLuxurySearchByLocationList } from '../MapView/queries';
import { shouldUseMockData } from '../../modules/utils';
import { SkeletonLoader } from '../SkeletonLoader';

// Use named rather than default exports.
export const ListView: FC<ListViewProps> = ({
  ctaLabel,
  showReviews,
  previousLabel,
  reviewsLabel,
  nextLabel,
  viewPort = 'viewPortL',
  filtersPayload,
  mapCenter,
  isGeoQuerySearch,
  requestID,
  currentLocale,
  client,
  getRegionFacets,
  propertyLabel,
  propertiesLabel,
  searchRadius,
  isAuthorModeEnabled,
}) => {
  //pagination

  const ITEMS_PER_PAGE = 9;
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [searchResults, setSearchResults] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Loads list-view GraphQL-shaped mock data from `./__mock__/ListView.model` when author/dev rules allow mocks.
   * Keeps the large mock out of the main bundle via dynamic `import()`.
   *
   * @param isAuthorMode - Passed to `shouldUseMockData`; when false or mocks are disabled, returns `{}`.
   * @returns `listViewMockData` from the mock module, or `{}` when mocks must not be used.
   */
  const GetMockData = async (isAuthorMode: boolean) => {
    if (shouldUseMockData(isAuthorMode)) {
      const mockData = await import('./__mock__/ListView.model');
      return mockData.listViewMockData;
    }
    return {};
  };

  /**
   * Fetches list-view data from GraphQL or mock data based on author/dev mode.
   * Uses `fetchHDData` for production, `GetMockData` for dev/author.
   *
   * @param query - GraphQL query to execute.
   * @param variables - Variables to pass to the query.
   * @returns Fetched data or mock data, or `[]` when no data is available.
   */
  const getData = async (query: any, variables: any) => {
    setIsLoading(true);
    try {
      const data = isAuthorModeEnabled
        ? await GetMockData(isAuthorModeEnabled)
        : await fetchHDData(client, query, {
            variables: variables,
            context: {
              headers: {
                'x-request-id': requestID,
                'accept-language': currentLocale?.replace('_', '-'),
              },
            },
          });

      const resolved = data;
      setSearchResults(resolved ?? []);
    } catch {
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  //logic to group by field
  const getGroupedByField = (region: string) => {
    const groupedByCityRegions = ['united-states', 'mexico', 'canada'];
    return groupedByCityRegions.includes(region) ? 'city' : 'country';
  };
  const groupedByField = getGroupedByField(filtersPayload[7]?.dimensions?.toString());

  useEffect(() => {
    //TODO : need to update this condition....
    // if (true) {
    const locQueryvariables = {
      search: {
        options: { numberInParty: 1 },
        facets: {
          terms: [...filtersPayload],
        },
      },
      limit: 9,
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
      sort: {
        fields: [
          {
            field: groupedByField?.toUpperCase(),
            direction: 'ASC',
          },
        ],
      },
    };
    const distance = Number(searchRadius);
    const geoQueryVariables = {
      search: {
        latitude: mapCenter?.lat,
        longitude: mapCenter?.lng,
        distance: distance,
        distanceUnit: 'MILES',
        options: {
          numberInParty: 1,
        },
        facets: {
          terms: [...filtersPayload],
        },
      },
      limit: 9,
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
      sort: {
        fields: [
          {
            field: groupedByField?.toUpperCase(),
            direction: 'ASC',
          },
        ],
      },
    };

    if (isGeoQuerySearch) {
      getData(phoenixLuxurySearchByGeoQueryList, geoQueryVariables);
    } else {
      getData(phoenixLuxurySearchByLocationList, locQueryvariables);
    }
    // }
  }, [filtersPayload, currentPage]);

  const totalCards =
    searchResults?.search?.properties?.searchByLocation?.total ??
    searchResults?.search?.properties?.searchByGeolocation?.total ??
    0;

  const groupedByCityOrCountry = (obj: any) => {
    if (groupedByField === 'country') {
      return obj[groupedByField]?.description;
    } else {
      return obj[groupedByField];
    }
  };

  const groupedMarkers = useMemo(() => {
    const listViewRes =
      searchResults?.search?.properties?.searchByLocation?.edges ??
      searchResults?.search?.properties?.searchByGeolocation?.edges ??
      [];

    if (!listViewRes || !Array.isArray(listViewRes)) return {};

    return listViewRes?.reduce((accumulator: any, marker) => {
      const info = marker?.node?.contactInformation?.address || {};
      const key = groupedByCityOrCountry(info) || 'unknown';

      if (!accumulator[key]) accumulator[key] = [];
      accumulator[key].push(marker);

      return accumulator;
    }, {});
  }, [searchResults, groupedByField]);

  const flattenedMarkers = Object.entries(groupedMarkers)?.flatMap(([region, markerObj]) =>
    (markerObj as any)?.map((item: any) => ({ ...item, region }))
  );

  const paginatedGrouped: any = flattenedMarkers?.reduce((groups, location) => {
    if (!groups[groupedByCityOrCountry(location?.node?.contactInformation?.address)])
      groups[groupedByCityOrCountry(location?.node?.contactInformation?.address)] = [];
    groups[groupedByCityOrCountry(location?.node?.contactInformation?.address)].push(location);
    return groups;
  }, {});

  const containerRef = useRef<HTMLDivElement>(null);
  const previous = previousLabel || 'Previous';
  const next = nextLabel || 'Next';

  const onPageChange = (page: number) => {
    setCurrentPage(page);
    if (containerRef.current) {
      const navbarHeight = viewPort?.includes('Mobile') ? MOBILE_NAVBAR_HEIGHT : NAVBAR_HEIGHT;
      const containerTop = containerRef.current.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
      window.scrollTo({ top: containerTop, behavior: 'auto' });
    }
  };

  // logic to display selected region in list view
  const selectedRegion = filtersPayload[7]?.dimensions?.toString();
  const regionFacets = getRegionFacets();
  const displayRegion = (region: string | undefined) => {
    const buckets = regionFacets?.[0]?.buckets || [];
    const match = buckets?.find((bucket: any) => bucket.code === region);
    return match ? match?.description : region;
  };

  return (
    <StyledListView
      data-testid="luxury-ListView"
      data-component-name="m-luxury-ListView"
      aria-busy={isLoading}
      ref={containerRef}
    >
      {isLoading ? (
        <SkeletonLoader viewPort={viewPort} />
      ) : (
        <>
          {totalCards > 0 && (
            <div className="t-label-xs property-count my-3">
              {totalCards} {totalCards === 1 ? propertyLabel : propertiesLabel}
            </div>
          )}
          <div className="t-overline-normal pb-2">{displayRegion(selectedRegion)}</div>
          <div>
            {Object.entries(paginatedGrouped)?.map(([region, regionMarkers]) => (
              <div className="d-block mb-2 mb-md-0 paginated-group" key={region}>
                <h3 className="pb-2 t-subtitle-l">{region}</h3>
                <ul className="d-flex flex-wrap justify-content-space-between">
                  {(regionMarkers as any)?.map((item: any, index: React.Key | null | undefined) => (
                    <li className="col-xl-4 col-md-6 col-12 px-0 pb-3" key={index}>
                      <HotelCard
                        currentLocale={currentLocale}
                        props={item}
                        variation="list-view"
                        ctaLabel={ctaLabel}
                        showReviews={showReviews}
                        reviewsLabel={reviewsLabel}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="">
            <Pagination
              currentPage={currentPage}
              totalCount={totalCards}
              pageSize={ITEMS_PER_PAGE}
              labels={{ previous, next }}
              onPageChange={(page: number) => onPageChange(page)}
              // isDirectionRightToLeft={direction}
            />
          </div>
        </>
      )}
    </StyledListView>
  );
};
